import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Skeleton } from "@mui/material";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import EmptyState from "global/components/EmptyState/EmptyState";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import {
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { formatMoney } from "helpers/perSeatHelpers";
import { fillCopy } from "helpers/smsCreditHelpers";
import { hasAccessTo } from "layouts/Config";
import { fetchSmsCreditActivity } from "Services/CommonServices";
import { SmsCreditActivityEntry } from "types/smsCredit";
import { SmsCreditState } from "hooks/use-sms-credits";
import { useFeature } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import myPlanTokens from "screens/Settings/MyPlan/MyPlan.tokens";
import {
  planCardSx,
  sectionDescSx,
  sectionHeaderActionSx,
  sectionHeaderSx,
  sectionTitleSx,
  statLabelSx,
  statValueSx,
} from "screens/Settings/MyPlan/MyPlan.styles";
import { formatInvoiceDate } from "screens/Settings/MyPlan/components/MyPlanHelper";
import BuySmsCreditsPanel from "./BuySmsCreditsPanel";
import smsCreditsTokens from "./SmsCredits.tokens";
import {
  activityDateSx,
  activityDeltaSx,
  activityDetailSx,
  activityHeaderSx,
  activityLabelSx,
  activityRowSx,
  activityStackSx,
  badgeSx,
  balanceUnitSx,
  buyToggleSx,
  loadFailedRetrySx,
  loadFailedSx,
  loadFailedTextSx,
  loadMoreBarSx,
  loadMoreButtonSx,
  lowStripIconSx,
  lowStripLinkSx,
  lowStripSx,
  lowStripTextSx,
  meterFillSx,
  meterTrackSx,
  statCellSx,
  titleRowSx,
  statStripSx,
} from "./SmsCredits.styles";

const t = myPlanTokens;
const copy = strings.smsCredits;

interface Props {
  /** Wallet state, owned by the screen so a purchase can refresh it in place. */
  credits: SmsCreditState;
  /** Default card's last four, for the buy panel's reassurance line. */
  cardLast4?: string;
  /** Take the customer to the saved cards after a payment failure. */
  onManagePaymentMethods: () => void;
}

/** The alert glyph on the low-balance strip, drawn as the design draws it. */
const AlertCircleGlyph = (props: { sx?: any }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    sx={props.sx}
  >
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
    <circle cx="12" cy="12" r="9" />
  </Box>
);

/**
 * What this line was. `type` is an open set — an unknown value still carries a
 * date and a signed credit change, so it renders with a generic label rather
 * than being dropped, which would silently hide spend from the customer.
 */
const activityLabel = (entry: SmsCreditActivityEntry): string => {
  switch (entry.type) {
    case "CREDIT_PURCHASE":
      return copy.typePurchase;
    case "CREDIT_GRANT":
      return copy.typeGrant;
    case "CREDIT_REVOKE":
      return copy.typeRevoke;
    case "SIGNING_LINK":
      return copy.typeSigningLink;
    case "VERIFICATION_CODE":
      // A name is only resolved on a single-recipient line.
      return entry.recipientName
        ? fillCopy(copy.typeVerification, { name: entry.recipientName })
        : copy.typeVerificationGeneric;
    case "SMS_SENT":
      return copy.typeSmsSent;
    default:
      return copy.typeUnknown;
  }
};

/**
 * The DETAIL column: what a purchase cost, or which document a send was for
 * and how many people it reached. Absent parts drop out — a text sent outside
 * a signing request has no document title.
 */
const activityDetail = (entry: SmsCreditActivityEntry): string => {
  if (entry.quantity !== undefined && entry.amount !== undefined) {
    return fillCopy(copy.purchaseDetail, {
      quantity: entry.quantity.toLocaleString(),
      amount: formatMoney(entry.amount, entry.currency),
    });
  }
  const parts = [entry.documentTitle];
  if ((entry.messageCount ?? 0) > 1) {
    parts.push(fillCopy(copy.signersDetail, { count: entry.messageCount! }));
  }
  return parts.filter(Boolean).join(" · ");
};

/**
 * The prepaid SMS wallet on Settings → My Plan (`Settings.dc.html`, "SMS
 * credits"), between the plan card and Payment Methods.
 *
 * Sold by quantity as a one-time add-on, so nothing here renews and there is
 * no "remove" — a used-up purchase has nothing to cancel. The card hides
 * entirely when the plan has no SMS at all, and hides only the Buy control
 * when credits aren't on sale in this account's billing currency: offering a
 * purchase the backend will refuse is worse than showing none.
 */
const SmsCredits = ({ credits, cardLast4, onManagePaymentMethods }: Props) => {
  const { balance, credits: wallet, isLoading, isUnavailable, hasFailed } =
    credits;
  /**
   * Whether SMS is in this account's plan at all. The balance endpoint's `403`
   * below is the authority, but it only arrives after a round trip — checking
   * the already-loaded feature map first stops the card flashing into view on
   * a plan that can never use it. (`useFeature` reports true until the map
   * loads, so this never hides the card spuriously.)
   */
  const hasSmsFeature = useFeature(FeatureKeys.ENVELOPE_SEND_SMS);
  const [buyOpen, setBuyOpen] = useState<boolean>(false);
  const [activity, setActivity] = useState<SmsCreditActivityEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState<boolean>(true);
  /** Appending a page, as opposed to the first load — the rows stay put. */
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  /** Last page actually loaded. 1-based, matching the endpoint. */
  const [page, setPage] = useState<number>(1);
  /**
   * A short page is the last page. The feed has no total, so this is the only
   * signal there is — asking for one more page just to see it come back empty
   * costs a request the endpoint over-reads to serve.
   */
  const [hasMore, setHasMore] = useState<boolean>(false);
  // Memoised: a fresh promise on every render would remount Elements.
  const stripePromise = useMemo(() => loadStripe(urls.STRIPE_PUBLIC_KEY), []);

  /**
   * An empty `packs` means nothing is on sale in this account's billing
   * currency — same outcome as a missing `addOn`: hide the control rather than
   * offer a purchase that would be refused.
   */
  const canBuy =
    hasAccessTo(strings.resources.billing, strings.addPermission) &&
    Boolean(wallet?.addOn?.packs?.length);

  const pageSize = smsCreditsTokens.activityLimit;

  const loadActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      const rows = await fetchSmsCreditActivity(1, pageSize);
      setActivity(rows);
      setPage(1);
      setHasMore(rows.length === pageSize);
    } catch {
      // Activity is a record, not a control — an unreadable list costs the
      // customer nothing they can't get from the invoice list below it.
      setActivity([]);
      setPage(1);
      setHasMore(false);
    } finally {
      setActivityLoading(false);
    }
  }, [pageSize]);

  /**
   * Next page, appended. The server pages by 1-based page number and works out
   * the offset itself, so the count of rows already held is not what it wants —
   * `page` is only advanced once the request succeeds, leaving a retry pointed
   * at the page that failed.
   */
  const loadMoreActivity = useCallback(async () => {
    try {
      setLoadingMore(true);
      const next = page + 1;
      const rows = await fetchSmsCreditActivity(next, pageSize);
      setActivity((current) => [...current, ...rows]);
      setPage(next);
      setHasMore(rows.length === pageSize);
    } catch {
      // Keep what is already on screen and stop offering more.
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (isUnavailable || !hasSmsFeature) return;
    loadActivity();
  }, [isUnavailable, hasSmsFeature, loadActivity]);

  /**
   * A completed purchase. `pending` means 3-D Secure was approved but the
   * credits arrive with Stripe's notification to the backend rather than with
   * the response, so the balance is polled instead of being taken on trust.
   */
  const handlePurchased = async (result: {
    quantity: number;
    balance: number;
    pending: boolean;
    invoiceUrl?: string;
  }) => {
    setBuyOpen(false);
    loadActivity();

    if (!result.pending) {
      credits.refresh();
      openSuccessNotification(
        fillCopy(copy.purchaseDone, {
          quantity: result.quantity.toLocaleString(),
          balance: result.balance.toLocaleString(),
        }),
      );
      return;
    }

    const settled = await credits.waitForBalanceChange(result.balance);
    if (settled === null) {
      openSuccessNotification(copy.purchasePending);
      return;
    }
    openSuccessNotification(
      fillCopy(copy.purchaseDone, {
        quantity: result.quantity.toLocaleString(),
        balance: settled.toLocaleString(),
      }),
    );
  };

  // The plan has no SMS at all — hide every affordance rather than advertise
  // something this account cannot use.
  if (!hasSmsFeature || isUnavailable) return null;

  const purchased = wallet?.purchasedTotal ?? 0;
  const isLow = !isLoading && balance <= strings.SMS_CREDIT_LOW_BALANCE_THRESHOLD;
  /**
   * How much of what was bought is still unspent. Floored at 2% so an almost
   * empty wallet still draws a sliver rather than nothing, and clamped at 100
   * for the case where a support grant pushes the balance past what was paid for.
   */
  const meterPercent = purchased
    ? Math.min(100, Math.max(2, Math.round((balance / purchased) * 100)))
    : 0;

  /** Newest purchase in the feed — the API has no "last purchase" field. */
  const lastPurchase = activity.find(
    (entry) => entry.type === "CREDIT_PURCHASE" && entry.quantity !== undefined,
  );

  const stat = (
    label: string,
    value: React.ReactNode,
    note: React.ReactNode,
    index: number,
    meter?: React.ReactNode,
  ) => (
    <Box sx={statCellSx(index, 3)}>
      <Box sx={statLabelSx}>{label}</Box>
      {isLoading ? (
        <Skeleton variant="text" width={90} sx={statValueSx(t.color.ink)} />
      ) : (
        <Box sx={statValueSx(t.color.ink)}>{value}</Box>
      )}
      {meter}
      <Box sx={{ fontSize: t.font.statCaption, color: t.color.muted }}>
        {note}
      </Box>
    </Box>
  );

  const activityRow = (entry: SmsCreditActivityEntry, index: number) => {
    const isPositive = entry.credits >= 0;
    const detail = activityDetail(entry);
    return (
      <Box
        key={`${entry.type}-${entry.date}-${entry.referenceId ?? index}`}
        sx={activityRowSx(index === activity.length - 1)}
      >
        {/* Below `md` the four columns stack into a label/meta block; the
            credit change stays on the right where it is scanned for. */}
        <Box sx={activityStackSx}>
          <Box component="span" sx={activityLabelSx}>
            {activityLabel(entry)}
          </Box>
          <Box
            component="span"
            sx={[activityDateSx, { display: { md: "none" } }] as any}
          >
            {[formatInvoiceDate(entry.date), detail].filter(Boolean).join(" · ")}
          </Box>
        </Box>
        <Box
          component="span"
          sx={[activityDateSx, { display: { xs: "none", md: "block" } }] as any}
        >
          {formatInvoiceDate(entry.date)}
        </Box>
        <Box
          component="span"
          sx={
            [activityDetailSx, { display: { xs: "none", md: "block" } }] as any
          }
        >
          {detail}
        </Box>
        {/* `credits` arrives already signed — spend is negative, so this only
            has to pick the tone and add the plus. */}
        <Box component="span" sx={activityDeltaSx(isPositive)}>
          {`${isPositive ? "+" : ""}${entry.credits.toLocaleString()}`}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={planCardSx}>
      <Box sx={sectionHeaderSx(false)}>
        <Box>
          <Box sx={titleRowSx}>
            <Box component="h2" sx={sectionTitleSx}>
              {copy.sectionTitle}
            </Box>
            {!isLoading && (
              <Box component="span" sx={badgeSx(isLow)}>
                {isLow ? copy.badgeLow : copy.badgeActive}
              </Box>
            )}
          </Box>
          <Box sx={sectionDescSx}>{copy.sectionDesc}</Box>
        </Box>
        {canBuy && (
          <Box sx={sectionHeaderActionSx}>
            <Button
              variant="contained"
              disableRipple
              onClick={() => setBuyOpen((open) => !open)}
              disabled={isLoading}
              aria-expanded={buyOpen}
              sx={buyToggleSx(buyOpen)}
            >
              {buyOpen ? copy.closeLabel : copy.buyLabel}
            </Button>
          </Box>
        )}
      </Box>

      {/* A failed read is not an empty wallet. Showing zeroes and quietly
          dropping the Buy control would misreport the account's balance and
          hide the actual fault, so the card says what happened and offers a
          retry instead. */}
      {hasFailed ? (
        <Box sx={loadFailedSx} role="alert">
          <Box component="span" sx={loadFailedTextSx}>
            {copy.loadFailed}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => {
              credits.refresh();
              loadActivity();
            }}
            sx={loadFailedRetrySx}
          >
            {copy.retry}
          </Box>
        </Box>
      ) : (
      <Box sx={statStripSx}>
        {stat(
          copy.balanceLabel,
          <>
            {balance.toLocaleString()}
            <Box component="span" sx={balanceUnitSx}>
              {` ${copy.balanceUnit}`}
            </Box>
          </>,
          fillCopy(copy.balanceNote, { purchased: purchased.toLocaleString() }),
          0,
          <Box sx={meterTrackSx}>
            <Box sx={meterFillSx(meterPercent, isLow)} />
          </Box>,
        )}
        {stat(
          copy.usedLabel,
          (wallet?.consumedTotal ?? 0).toLocaleString(),
          copy.usedNote,
          1,
        )}
        {stat(
          copy.lastPurchaseLabel,
          lastPurchase
            ? (lastPurchase.quantity ?? 0).toLocaleString()
            : copy.noPurchaseYet,
          lastPurchase
            ? fillCopy(copy.lastPurchaseNote, {
                date: formatInvoiceDate(lastPurchase.date),
                amount: formatMoney(
                  lastPurchase.amount ?? 0,
                  lastPurchase.currency,
                ),
              })
            : copy.noPurchaseNote,
          2,
        )}
      </Box>
      )}

      {isLow && !hasFailed && (
        <Box sx={lowStripSx} role="status">
          <AlertCircleGlyph sx={lowStripIconSx} />
          <Box component="span" sx={lowStripTextSx}>
            {fillCopy(copy.lowBalance, { balance: balance.toLocaleString() })}{" "}
            {copy.lowStripText}
          </Box>
          {canBuy && (
            <Box
              component="button"
              type="button"
              onClick={() => setBuyOpen(true)}
              sx={lowStripLinkSx}
            >
              {copy.topUpNow}
            </Box>
          )}
        </Box>
      )}

      {/* The panel needs Stripe for 3-D Secure (`handleNextAction`), which only
          resolves inside an Elements provider — this card's own, so the
          screen's add-card provider stays untouched. */}
      {buyOpen && wallet?.addOn?.packs?.length ? (
        <Elements stripe={stripePromise}>
          <BuySmsCreditsPanel
            open={buyOpen}
            onClose={() => setBuyOpen(false)}
            addOn={wallet.addOn}
            cardLast4={cardLast4}
            onPurchased={handlePurchased}
            onManagePaymentMethods={onManagePaymentMethods}
            onOffSale={(message) => {
              // Re-read the offer: the next response drops `addOn`, which is
              // what actually removes the Buy control.
              credits.refresh();
              openErrorNotification(message);
            }}
          />
        </Elements>
      ) : null}

      <Box sx={activityHeaderSx}>
        <Box component="span">{copy.activityColActivity}</Box>
        <Box component="span">{copy.activityColDate}</Box>
        <Box component="span">{copy.activityColDetail}</Box>
        <Box component="span" sx={{ textAlign: "right" }}>
          {copy.activityColCredits}
        </Box>
      </Box>

      {activityLoading ? (
        [...Array(3)].map((_, index) => (
          <Box key={index} sx={activityRowSx(index === 2)}>
            <Box sx={activityStackSx}>
              <Skeleton variant="text" width="42%" sx={activityLabelSx} />
            </Box>
            <Skeleton variant="text" width={54} sx={activityDeltaSx(true)} />
          </Box>
        ))
      ) : activity.length > 0 ? (
        <>
          {activity.map(activityRow)}
          {hasMore && (
            <Box sx={loadMoreBarSx}>
              <Button
                disableRipple
                onClick={loadMoreActivity}
                disabled={loadingMore}
                sx={loadMoreButtonSx}
              >
                {loadingMore ? copy.loadingMore : copy.loadMore}
              </Button>
            </Box>
          )}
        </>
      ) : (
        /* Read-only record, so no action — the Buy control lives in the header
           above and repeating it here would give the block two. */
        <EmptyState
          icon="invoice"
          accent="amber"
          title={copy.historyEmptyTitle}
          body={copy.historyEmptyBody}
        />
      )}
    </Box>
  );
};

export default SmsCredits;
